/**
 * rs-product-mto-input.js
 *
 * Custom element for Made-to-Order customization text inputs.
 * Manages input validation, character restrictions, icon/motif encoding,
 * and ATC gate synchronization.
 *
 * Loaded globally via head.liquid. Works in two modes:
 *  - PDP: syncs properties and gates ATC via RSProduct
 *  - Quick-add (no RSProduct): manages hidden form inputs directly
 *    and disables/enables the submit button
 */
if (!customElements.get('rs-product-mto-input')) {
  customElements.define('rs-product-mto-input', class extends HTMLElement {
    connectedCallback() {
      var configEl = this.querySelector('[data-mto-config]');
      if (!configEl) return;
      this.config = JSON.parse(configEl.textContent);
      this.iconsData = null;
      this.cursorPos = [0, 0];
      this._isQuickAdd = !!this.closest('[data-quick-add-modal]');

      var iconsEl = this.querySelector('[data-mto-icons]');
      if (iconsEl) this.iconsData = JSON.parse(iconsEl.textContent);

      if (this._isQuickAdd || window.RSProduct) {
        this._init();
      } else {
        window.addEventListener('rs:product-core-ready', () => this._init(), { once: true });
      }
    }

    disconnectedCallback() {
      if (this._isQuickAdd) return;
      const config = this.config;
      queueMicrotask(() => {
        if (document.querySelector('rs-product-mto-input')) return;
        if (!window.RSProduct) return;
        window.RSProduct.removeGate('mto-input');
        if (config) {
          for (var i = 1; i <= config.inputCount; i++) {
            window.RSProduct.removeProperty(this._customizationKey(i));
          }
        }
        window.RSProduct.removeProperty('_Customization');
      });
    }

    _init() {
      this._bindInputs();
      this._bindIcons();
      this._bindIconScroll();
      this._bindInfoPopups();
      this._bindPreviewButtons();
      this._registerGate();
      this._validate();
      this._syncPreviewButtons();
    }

    get _useRSProduct() {
      return !this._isQuickAdd && !!window.RSProduct;
    }

    _getInputs() {
      return this.querySelectorAll('[data-mto-field]');
    }

    _getFieldConfig(index) {
      return this.config.fields.find(f => f.index === index);
    }

    _customizationKey(index) {
      return '_Customization_' + index;
    }

    _registerGate() {
      if (!this._useRSProduct) return;
      window.RSProduct.registerGate('mto-input', {
        disabled: this.config.hasRequiredField,
        text: window.theme?.strings?.enterCustomization || 'Please Enter Your Customization',
        priority: 20,
      });
    }

    _bindInputs() {
      this._getInputs().forEach(input => {
        const fieldIndex = parseInt(input.dataset.mtoField);
        const fieldConfig = this._getFieldConfig(fieldIndex);

        input.addEventListener('input', () => {
          if (fieldConfig) {
            if (fieldConfig.lettercase === 'uppercase') {
              const pos = input.selectionStart;
              input.value = input.value.toUpperCase();
              input.setSelectionRange(pos, pos);
            } else if (fieldConfig.lettercase === 'lowercase') {
              const pos = input.selectionStart;
              input.value = input.value.toLowerCase();
              input.setSelectionRange(pos, pos);
            }
            this._restrictChars(input);
          }
          this._validate();
          this._syncProperties();
          this._syncPreviewButtons();
        });

        input.addEventListener('click', () => {
          this.cursorPos = [input.selectionStart, input.selectionEnd];
          this._activeInput = input;
        });

        input.addEventListener('keyup', () => {
          this.cursorPos = [input.selectionStart, input.selectionEnd];
        });
      });
    }

    _bindPreviewButtons() {
      this.querySelectorAll('[data-mto-preview]').forEach(button => {
        button.addEventListener('click', () => this._scrollToPreview());
      });
    }

    _syncPreviewButtons() {
      this.querySelectorAll('[data-mto-preview]').forEach(button => {
        const input = this.querySelector(`[data-mto-field="${button.dataset.mtoPreview}"]`);
        const visible = !!input && !!input.value.trim();
        button.hidden = !visible;
        button.closest('.rs-mto__header')?.classList.toggle('rs-mto__header--with-preview', visible);
      });
    }

    _scrollToPreview() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    _restrictChars(input) {
      const pattern = input.getAttribute('pattern');
      if (!pattern) return;

      try {
        const before = input.value;
        const regex = new RegExp('^' + pattern + '$');
        const filtered = before.split('').filter(ch => {
          if (regex.test(ch)) return true;
          if (ch >= 'a' && ch <= 'z' && regex.test(ch.toUpperCase())) return true;
          if (ch >= 'A' && ch <= 'Z' && regex.test(ch.toLowerCase())) return true;
          return false;
        }).join('');

        if (before !== filtered) {
          const cursorPos = input.selectionStart;
          const validBefore = before.substring(0, cursorPos).split('').filter(ch => {
            if (regex.test(ch)) return true;
            if (ch >= 'a' && ch <= 'z' && regex.test(ch.toUpperCase())) return true;
            if (ch >= 'A' && ch <= 'Z' && regex.test(ch.toLowerCase())) return true;
            return false;
          }).length;
          input.value = filtered;
          input.setSelectionRange(validBefore, validBefore);
        }
      } catch (e) { /* pattern invalid, skip */ }
    }

    _validate() {
      let allValid = true;
      const inputs = this._getInputs();

      inputs.forEach(input => {
        const fieldIndex = parseInt(input.dataset.mtoField);
        const fieldConfig = this._getFieldConfig(fieldIndex);
        if (!fieldConfig) return;

        const value = input.value;
        const isEmpty = value.length === 0;
        const isOptional = fieldConfig.required !== 'true';
        const label = this.querySelector('[data-mto-label="' + fieldIndex + '"]');

        let isValid = true;

        if (isEmpty) {
          isValid = isOptional;
          input.classList.remove('is-invalid');
        } else {
          const meetsMin = value.length >= fieldConfig.minLength;
          const pattern = input.getAttribute('pattern');
          let meetsPattern = true;
          if (pattern) {
            try {
              meetsPattern = new RegExp('^' + pattern + '$').test(value);
            } catch (e) { /* skip */ }
          }
          isValid = meetsMin && meetsPattern;
          input.classList.toggle('is-invalid', !isValid);
        }

        if (label) {
          label.classList.toggle('is-invalid', !isEmpty && !isValid);
        }

        if (!isValid) allValid = false;
      });

      if (this._useRSProduct) {
        window.RSProduct.updateGate('mto-input', {
          disabled: !allValid,
          text: allValid
            ? (window.theme?.strings?.addToCart || 'Add to Cart')
            : (window.theme?.strings?.enterCustomization || 'Please Enter Your Customization'),
        });
      } else {
        this._gateSubmitButton(!allValid);
      }
    }

    _syncProperties() {
      const inputs = this._getInputs();
      const hasIcons = this.config.hasIcons;

      if (this._useRSProduct) {
        if (hasIcons) {
          const firstInput = inputs[0];
          if (firstInput && firstInput.value.trim()) {
            window.RSProduct.setProperty('_Customization', this._encodeForCart(firstInput.value));
          } else {
            window.RSProduct.removeProperty('_Customization');
          }
        } else {
          inputs.forEach(input => {
            const fieldIndex = parseInt(input.dataset.mtoField);
            const value = input.value.trim();
            if (value) {
              window.RSProduct.setProperty(this._customizationKey(fieldIndex), value);
            } else {
              window.RSProduct.removeProperty(this._customizationKey(fieldIndex));
            }
          });
        }
        return;
      }

      const form = this._findForm();
      if (!form) return;

      if (hasIcons) {
        const firstInput = inputs[0];
        const value = firstInput && firstInput.value.trim()
          ? this._encodeForCart(firstInput.value) : '';
        this._setFormProperty(form, '_Customization', value);
      } else {
        inputs.forEach(input => {
          const fieldIndex = parseInt(input.dataset.mtoField);
          const value = input.value.trim();
          this._setFormProperty(form, this._customizationKey(fieldIndex), value);
        });
      }
    }

    _findForm() {
      const productInfo = this.closest('product-info');
      return productInfo ? productInfo.querySelector('form[data-product-form]') : null;
    }

    _setFormProperty(form, key, value) {
      const name = 'properties[' + key + ']';
      let hidden = form.querySelector('input[name="' + name + '"]');
      if (value) {
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = name;
          form.appendChild(hidden);
        }
        hidden.value = value;
      } else if (hidden) {
        hidden.remove();
      }
    }

    _gateSubmitButton(disabled) {
      const form = this._findForm();
      if (!form) return;
      const btn = form.querySelector('[data-add-to-cart]');
      if (!btn) return;

      const textEl = btn.querySelector('[data-add-to-cart-text]') || btn.querySelector('.btn__text');
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        if (textEl) textEl.textContent = window.theme?.strings?.enterCustomization || 'Please Enter Your Customization';
      } else {
        btn.removeAttribute('disabled');
        if (textEl) textEl.textContent = btn.dataset.preorder === 'true'
          ? (window.theme?.strings?.preOrder || 'Pre-order')
          : (window.theme?.strings?.addToCart || 'Add to Cart');
      }
    }

    _encodeForCart(value) {
      if (!this.iconsData) return value;
      return value.split('').map(char => {
        const code = '\\u' + ('000' + char.charCodeAt(0).toString(16)).slice(-4);
        const icon = this.iconsData.find(ic => ic.utfChar === code);
        return icon ? icon.cartChar : char;
      }).join('');
    }

    _decodeUtfChar(str) {
      return str.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      );
    }

    _bindInfoPopups() {
      this.querySelectorAll('[data-mto-info-trigger]').forEach(trigger => {
        const idx = trigger.dataset.mtoInfoTrigger;
        const popup = this.querySelector('[data-mto-info-popup="' + idx + '"]');
        const closeBtn = this.querySelector('[data-mto-info-close="' + idx + '"]');
        if (!popup) return;

        trigger.addEventListener('click', () => {
          const isOpen = !popup.hidden;
          popup.hidden = isOpen;
          trigger.classList.toggle('is-active', !isOpen);
        });
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            popup.hidden = true;
            trigger.classList.remove('is-active');
          });
        }
      });
    }

    _bindIconScroll() {
      this.querySelectorAll('[data-mto-icons-nav]').forEach(nav => {
        const grid = nav.querySelector('[data-mto-icons-grid]');
        const viewport = nav.querySelector('.rs-mto__icons-viewport');
        const prevBtn = nav.querySelector('.rs-mto__icons-arrow--prev');
        const nextBtn = nav.querySelector('.rs-mto__icons-arrow--next');
        if (!grid || !viewport || !prevBtn || !nextBtn) return;

        const icons = grid.querySelectorAll('[data-utf-char]');
        const gap = 6;
        const btnWidth = icons[0] ? icons[0].offsetWidth : 25;
        viewport.style.width = (3 * btnWidth + 2 * gap) + 'px';

        let page = 0;
        const totalPages = Math.ceil(icons.length / 3);

        const updateArrows = () => {
          prevBtn.classList.toggle('is-hidden', page === 0);
          nextBtn.classList.toggle('is-hidden', page >= totalPages - 1);
        };

        const scrollTo = (newPage) => {
          page = Math.max(0, Math.min(newPage, totalPages - 1));
          grid.style.transform = 'translateX(-' + (page * 3 * (btnWidth + gap)) + 'px)';
          updateArrows();
        };

        prevBtn.addEventListener('click', () => scrollTo(page - 1));
        nextBtn.addEventListener('click', () => scrollTo(page + 1));

        updateArrows();
      });
    }

    _bindIcons() {
      if (!this.iconsData) return;

      this.querySelectorAll('[data-utf-char]').forEach(btn => {
        btn.addEventListener('click', () => {
          const input = this._activeInput || this._getInputs()[0];
          if (!input) return;
          if (input.value.length >= input.maxLength) return;

          const decoded = this._decodeUtfChar(btn.dataset.utfChar);
          const start = this.cursorPos[0];
          const end = this.cursorPos[1];

          if (start === end) {
            input.value = input.value.substring(0, start) + decoded + input.value.substring(end);
          } else {
            input.value = input.value.substring(0, start) + decoded + input.value.substring(end);
          }

          const newPos = start + decoded.length;
          this.cursorPos = [newPos, newPos];
          input.focus();
          input.setSelectionRange(newPos, newPos);

          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      });
    }
  });
}
